/**
 * Kubernetes-node pieces of a box row (#804): the node action menu items
 * and the expanded node details.
 *
 * These used to make up a standalone <NodeRow> in a Kubernetes-only table
 * next to a separate "Provisioning Nodes" card, so one box showed up twice
 * and its recovery actions lived on the other card. The joined <BoxRow>
 * renders them now; the node actions are cordon/uncordon/drain/taints,
 * reboot, diagnostics and shutdown. All but Diagnostics are Admin-only, as the
 * BFF gates them (JSS#970).
 */
import { Role, canActAs } from '@jeket-cloud/types/auth';
import type { ClusterNode } from '@/types';
import { RoleGate } from '@/components/auth/RoleGate';

function formatBytes(str: string): string {
  if (!str) return '—';
  const ki = str.replace(/Ki$/, '');
  if (ki !== str) {
    const bytes = parseInt(ki, 10) * 1024;
    const gb = bytes / (1024 * 1024 * 1024);
    return gb >= 1 ? `${gb.toFixed(1)} Gi` : `${(bytes / (1024 * 1024)).toFixed(0)} Mi`;
  }
  return str;
}

/** Parse K8s CPU string to millicores */
function parseCpuMillis(val: string): number {
  if (!val) return 0;
  if (val.endsWith('n')) return parseInt(val) / 1_000_000;
  if (val.endsWith('u')) return parseInt(val) / 1_000;
  if (val.endsWith('m')) return parseInt(val);
  return parseFloat(val) * 1000;
}

function formatCpu(str: string): string {
  if (!str) return '—';
  const millis = parseCpuMillis(str);
  if (millis >= 1000) return `${(millis / 1000).toFixed(1)} cores`;
  return `${Math.round(millis)}m`;
}

/** Parse K8s memory string to bytes */
function parseMemBytes(val: string): number {
  if (!val) return 0;
  if (val.endsWith('Ki')) return parseInt(val) * 1024;
  if (val.endsWith('Mi')) return parseInt(val) * 1024 * 1024;
  if (val.endsWith('Gi')) return parseInt(val) * 1024 * 1024 * 1024;
  if (val.endsWith('Ti')) return parseInt(val) * 1024 * 1024 * 1024 * 1024;
  return parseInt(val);
}

function usagePct(usageStr: string, allocStr: string, parser: (v: string) => number): number {
  const used = parser(usageStr);
  const total = parser(allocStr);
  if (total <= 0) return 0;
  return Math.min((used / total) * 100, 100);
}

function UsageBar({ label, used, total, pct }: { label: string; used: string; total: string; pct: number }) {
  const color = pct > 90 ? 'bg-status-error' : pct > 70 ? 'bg-status-warning' : 'bg-gold';
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-silver-muted">{label}</span>
        <span className="text-silver-bright font-mono">{used} / {total} ({pct.toFixed(0)}%)</span>
      </div>
      <div className="h-1.5 bg-surface-elevated rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/**
 * Footnote under the Memory usage bar that explains why allocatable < physical
 * when it is. Pro-tier nodes reserve 1Gi hugepages at boot for SMPTE-2110
 * media pipelines (MXL/MTL), and hugepages reservations are subtracted from
 * `allocatable.memory`. Without this breakdown, operators see e.g. "14.4 Gi"
 * on a 32 GiB box and assume it's a bug — see issue #298.
 */
function MemoryBreakdown({
  capacity,
  allocatable,
}: {
  capacity: ClusterNode['capacity'];
  allocatable: ClusterNode['allocatable'];
}) {
  const physical = parseMemBytes(capacity.memory);
  const alloc = parseMemBytes(allocatable.memory);
  const hp1Gi = parseMemBytes(capacity.hugepages1Gi);
  const hp2Mi = parseMemBytes(capacity.hugepages2Mi);
  const reservedTotal = hp1Gi + hp2Mi;
  // Only render when there's a non-trivial gap to explain (>256 MiB) — small
  // kubelet/system reservations are uninteresting.
  if (physical <= 0 || physical - alloc < 256 * 1024 * 1024) return null;

  const parts: string[] = [`${formatBytes(capacity.memory)} physical`];
  if (hp1Gi > 0) parts.push(`${formatBytes(capacity.hugepages1Gi)} for 1Gi hugepages`);
  if (hp2Mi > 0) parts.push(`${formatBytes(capacity.hugepages2Mi)} for 2Mi hugepages`);
  if (reservedTotal === 0) parts.push(`~${formatBytes(`${Math.round((physical - alloc) / 1024)}Ki`)} kubelet/system reserved`);

  return (
    <div className="text-[10px] text-silver-muted mt-1 leading-snug">
      {parts.join(' · ')}
    </div>
  );
}

export interface NodeMenuItemsProps {
  node: ClusterNode;
  onAction: (node: ClusterNode, action: string) => void;
  /**
   * Engineer-tier "Talos diagnostics" trigger (#248). When provided, a
   * Diagnostics item is rendered; the caller only passes it to Engineers.
   */
  onDiagnostics?: (node: ClusterNode) => void;
  /**
   * Admin-tier "Edit taints" trigger (#256). Also wrapped in
   * `<RoleGate role={Role.Admin}>` here — the BFF and deployment-service
   * mirror enforce the actual security boundary.
   */
  onEditTaints?: (node: ClusterNode) => void;
  /** Close the surrounding menu after an item is chosen. */
  onClose: () => void;
}

const ITEM = 'w-full text-left px-3 py-1.5 text-xs text-silver-bright hover:bg-gold/10 transition-colors';

/** Whether <NodeMenuItems> renders any item for this user (JSS#970: else the row needs no menu for it). */
export function hasNodeMenuItems(roles: readonly string[], hasDiagnostics: boolean): boolean {
  return canActAs(roles, Role.Admin) || hasDiagnostics;
}

/**
 * The Kubernetes node actions, as items for a row's action menu. Below Admin
 * only Diagnostics remains, and only when `onDiagnostics` is passed:
 * `hasNodeMenuItems` says whether this renders anything at all.
 */
export function NodeMenuItems({ node, onAction, onDiagnostics, onEditTaints, onClose }: NodeMenuItemsProps) {
  const act = (action: string) => { onAction(node, action); onClose(); };
  return (
    <>
      {/* Admin-tier node ops (#256) — cordon/uncordon/drain/edit-taints,
        *  and reboot/shutdown, which the BFF gates at Admin on
        *  POST …/nodes/:nodeIp/action (JSS#970: they were ungated here, so a
        *  Viewer saw them and got a 403). Gated at the menu-item level. The
        *  BFF + deployment-service mirror are the actual security boundary;
        *  this hides the affordance from non-admins. */}
      <RoleGate role={Role.Admin}>
        {node.unschedulable ? (
          <button type="button" onClick={() => act('uncordon')} className={ITEM}>Uncordon</button>
        ) : (
          <button type="button" onClick={() => act('cordon')} className={ITEM}>Cordon</button>
        )}
        <button type="button" onClick={() => act('drain')} className={ITEM}>Drain</button>
        {onEditTaints && (
          <button type="button" onClick={() => { onEditTaints(node); onClose(); }} className={ITEM}>Edit taints</button>
        )}
        <button type="button" onClick={() => act('reboot')} className={ITEM}>Reboot</button>
      </RoleGate>
      {onDiagnostics && (
        <button
          type="button"
          onClick={() => { onDiagnostics(node); onClose(); }}
          className={`${ITEM} border-t border-surface-elevated`}
        >
          Diagnostics
        </button>
      )}
      <RoleGate role={Role.Admin}>
        <button
          type="button"
          onClick={() => act('shutdown')}
          className="w-full text-left px-3 py-1.5 text-xs text-status-error hover:bg-status-error/10 transition-colors"
        >
          Shutdown
        </button>
      </RoleGate>
    </>
  );
}

/** Expanded Kubernetes view of a node: hardware as K8s sees it, addresses, conditions, taints, usage. */
export function NodeDetails({ node }: { node: ClusterNode }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Hardware */}
      <div className="space-y-2">
        <h4 className="text-xs font-medium text-silver-muted uppercase tracking-wider">Node</h4>
        <div className="space-y-1 text-xs">
          <div className="flex justify-between gap-4"><span className="text-silver-muted">Name</span><span className="text-silver-bright font-mono">{node.name}</span></div>
          <div className="flex justify-between gap-4"><span className="text-silver-muted">CPU</span><span className="text-silver-bright font-mono">{node.hardware.cpu || node.architecture}</span></div>
          <div className="flex justify-between gap-4"><span className="text-silver-muted">Cores</span><span className="text-silver-bright font-mono">{node.hardware.cpuCores || node.capacity.cpu}</span></div>
          <div className="flex justify-between gap-4"><span className="text-silver-muted">Memory</span><span className="text-silver-bright font-mono">{formatBytes(node.capacity.memory)}</span></div>
          <div className="flex justify-between gap-4"><span className="text-silver-muted">OS</span><span className="text-silver-bright font-mono">{node.osImage}</span></div>
          <div className="flex justify-between gap-4"><span className="text-silver-muted">Kernel</span><span className="text-silver-bright font-mono">{node.kernelVersion}</span></div>
          <div className="flex justify-between gap-4"><span className="text-silver-muted">Runtime</span><span className="text-silver-bright font-mono">{node.containerRuntime}</span></div>
        </div>
      </div>

      {/* K8s Info */}
      <div className="space-y-2">
        <h4 className="text-xs font-medium text-silver-muted uppercase tracking-wider">Kubernetes</h4>
        <div className="space-y-1 text-xs">
          {node.addresses.map((a, i) => (
            <div key={i} className="flex justify-between gap-4"><span className="text-silver-muted">{a.type}</span><span className="text-silver-bright font-mono">{a.address}</span></div>
          ))}
          {node.hardware.gpus.length > 0 && (
            <div className="flex justify-between gap-4"><span className="text-silver-muted">GPUs</span><span className="text-silver-bright font-mono">{node.hardware.gpus.map(g => g.name).join(', ')}</span></div>
          )}
        </div>

        {/* Conditions */}
        {node.conditions.filter(c => c.status === 'True' && c.type !== 'Ready').length > 0 && (
          <div className="mt-2 space-y-1">
            <h4 className="text-xs font-medium text-status-warning uppercase tracking-wider">Conditions</h4>
            {node.conditions.filter(c => c.status === 'True' && c.type !== 'Ready').map((c, i) => (
              <div key={i} className="text-xs text-status-warning">{c.type}: {c.message}</div>
            ))}
          </div>
        )}

        {/* Taints */}
        {node.taints.length > 0 && (
          <div className="mt-2 space-y-1">
            <h4 className="text-xs font-medium text-silver-muted uppercase tracking-wider">Taints</h4>
            {node.taints.map((t, i) => (
              <div key={i} className="text-xs text-silver-muted font-mono">{t.key}={t.value}:{t.effect}</div>
            ))}
          </div>
        )}
      </div>

      {/* Resource Usage */}
      {node.usage && (
        <div className="md:col-span-2 space-y-2">
          <h4 className="text-xs font-medium text-silver-muted uppercase tracking-wider">Resource Usage</h4>
          <div className="grid grid-cols-2 gap-4">
            <UsageBar label="CPU" used={formatCpu(node.usage.cpu)} total={formatCpu(node.allocatable.cpu)} pct={usagePct(node.usage.cpu, node.allocatable.cpu, parseCpuMillis)} />
            <div>
              <UsageBar label="Memory" used={formatBytes(node.usage.memory)} total={formatBytes(node.allocatable.memory)} pct={usagePct(node.usage.memory, node.allocatable.memory, parseMemBytes)} />
              <MemoryBreakdown capacity={node.capacity} allocatable={node.allocatable} />
            </div>
          </div>
          {/* Disk + GPU */}
          <div className="grid grid-cols-2 gap-4 mt-1">
            {node.capacity.ephemeralStorage && (
              <div className="text-xs"><span className="text-silver-muted">Disk: </span><span className="text-silver-bright font-mono">{formatBytes(node.capacity.ephemeralStorage)}</span></div>
            )}
            {node.capacity.gpu && parseInt(node.capacity.gpu) > 0 && (
              <div className="text-xs"><span className="text-silver-muted">GPUs: </span><span className="text-silver-bright font-mono">{node.capacity.gpu}</span></div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
